import React from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem } from './command';
import { rankCategories, rankSubtypes, resolveSearch } from '../../domains/shared/services/RelationshipSuggestionService';
import type { DiagramType } from '../../lib/diagram/registries';

type Direction = 'top' | 'right' | 'bottom' | 'left';

interface ContextAwareQuickCreatePanelProps {
  diagramType: DiagramType;
  sourceNodeId: string;
  direction: Direction;
  onCreateNew: (params: { subtypeId: string; person: { sex: 'male'|'female'|'unknown'; deceased?: boolean } }) => void;
  onLinkExisting: (params: { subtypeId: string }) => void;
  variant?: 'create' | 'edit-edge';
  // no extra props
  onSubtypeSelect?: (subtypeId: string) => void;
  // Optional: context actions
  canAddParents?: boolean;
  onAddParents?: () => void;
}

export const ContextAwareQuickCreatePanel: React.FC<ContextAwareQuickCreatePanelProps> = ({
  diagramType,
  sourceNodeId,
  direction,
  onCreateNew,
  onLinkExisting,
  variant = 'create',
  onSubtypeSelect,
  canAddParents,
  onAddParents
}) => {
  const { t } = useTranslation('diagram');
  const [search, setSearch] = React.useState('');
  const [selectedPersonType, setSelectedPersonType] = React.useState<{ sex: 'male'|'female'|'unknown'; deceased?: boolean } | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = React.useState<string | null>(null);
  const [selectedSubtypeId, setSelectedSubtypeId] = React.useState<string | null>(null);
  const [isLinkingMode, setIsLinkingMode] = React.useState(false);

  const ctx = React.useMemo(() => ({ diagramType, sourceNodeId, direction }), [diagramType, sourceNodeId, direction]);

  const rankedCategories = React.useMemo(() => rankCategories(ctx), [ctx]);
  const rankedSubtypes = React.useMemo(() => (
    selectedCategoryId ? rankSubtypes(ctx, selectedCategoryId) : []
  ), [ctx, selectedCategoryId]);
  const selectedCategory = React.useMemo(() => (
    selectedCategoryId ? rankedCategories.find(c => c.category.id === selectedCategoryId)?.category || null : null
  ), [rankedCategories, selectedCategoryId]);
  const selectedSubtype = React.useMemo(() => (
    selectedSubtypeId ? rankedSubtypes.find(s => s.subtype.id === selectedSubtypeId)?.subtype || null : null
  ), [rankedSubtypes, selectedSubtypeId]);
  const searchResults = React.useMemo(() => {
    if (!search) return [] as ReturnType<typeof resolveSearch>;
    // If category is selected, limit search to that category's subtypes
    const results = resolveSearch(ctx, search);
    if (selectedCategoryId) {
      return results.filter(r => r.subtype.categoryId === selectedCategoryId);
    }
    return results;
  }, [ctx, search, selectedCategoryId]);

  const handleSubtypeSelect = (subtypeId: string) => {
    setSelectedSubtypeId(subtypeId);
    if (variant === 'edit-edge' && onSubtypeSelect) {
      onSubtypeSelect(subtypeId);
    }
    if (variant === 'create') {
      if (isLinkingMode) {
        onLinkExisting({ subtypeId });
        setIsLinkingMode(false);
        setSelectedPersonType(null);
      } else if (selectedPersonType) {
        onCreateNew({ subtypeId, person: selectedPersonType });
      }
    }
  };

  return (
    <div className="w-[260px]">
      <Command>
        <CommandInput data-testid="tid.ui.quickCreate.search" placeholder={t('shapes.quickCreateSearch')} value={search} onValueChange={setSearch} className="h-8 text-xs" />
        <CommandEmpty>
          {search ? t('quickCreatePanel.noMatches') : null}
        </CommandEmpty>

        {/* Breadcrumb / selection header */}
        {(selectedPersonType || selectedCategory || selectedSubtype) && (
          <div className="px-2 pt-1">
            <div className="inline-flex items-center gap-1 rounded-md bg-muted/60 px-2 py-1">
              {selectedPersonType && variant === 'create' && (
                <button
                  className="text-[12px] font-medium text-foreground/80 hover:underline"
                  onClick={() => { 
                    setSelectedPersonType(null); 
                    setSelectedCategoryId(null); 
                    setSelectedSubtypeId(null);
                    setIsLinkingMode(false);
                  }}
                >
                  {isLinkingMode ? t('quickCreatePanel.linkToExisting') : (selectedPersonType.deceased ? t('quickCreatePanel.deceased') : selectedPersonType.sex === 'male' ? t('quickCreatePanel.male') : selectedPersonType.sex === 'female' ? t('quickCreatePanel.female') : t('quickCreatePanel.unknown'))}
                </button>
              )}
              {selectedCategory && (
                <>
                  {selectedPersonType && variant === 'create' && <span className="text-muted-foreground">›</span>}
                  <button
                    className="text-[12px] font-medium text-foreground/80 hover:underline"
                    onClick={() => { 
                      setSelectedCategoryId(null); 
                      setSelectedSubtypeId(null);
                    }}
                  >
                    {t(selectedCategory.label as any, { defaultValue: selectedCategory.label })}
                  </button>
                </>
              )}
              {selectedSubtype && (
                <>
                  <span className="text-muted-foreground">›</span>
                  <button
                    className="text-[12px] font-medium text-foreground/80 hover:underline"
                    onClick={() => setSelectedSubtypeId(null)}
                  >
                    {t(selectedSubtype.label as any, { defaultValue: selectedSubtype.label })}
                  </button>
                </>
              )}
            </div>
          </div>
        )}

        {/* Context shortcuts (e.g., Add Parents when missing) */}
        {variant === 'create' && canAddParents && onAddParents && (
          <CommandGroup heading={t('quickCreatePanel.shortcutsHeading')}>
            <CommandItem
              key="add-parents"
              value="add-parents"
              onSelect={() => onAddParents()}
              className="text-xs cursor-pointer"
              data-testid="tid.ui.quickCreate.addParents"
            >
              {t('quickCreatePanel.addParents')}
            </CommandItem>
          </CommandGroup>
        )}

        {/* Person type selection (first step for create variant) */}
        {variant === 'create' && !selectedPersonType && !search && (
          <>
            <CommandGroup heading={t('quickCreatePanel.personTypeHeading')}>
              <CommandItem
                key="sex-male"
                value="sex-male"
                onSelect={() => setSelectedPersonType({ sex: 'male' })}
                className="text-xs cursor-pointer"
              >
                {t('quickCreatePanel.male')}
              </CommandItem>
              <CommandItem
                key="sex-female"
                value="sex-female"
                onSelect={() => setSelectedPersonType({ sex: 'female' })}
                className="text-xs cursor-pointer"
              >
                {t('quickCreatePanel.female')}
              </CommandItem>
              <CommandItem
                key="sex-unknown"
                value="sex-unknown"
                onSelect={() => setSelectedPersonType({ sex: 'unknown' })}
                className="text-xs cursor-pointer"
              >
                {t('quickCreatePanel.unknown')}
              </CommandItem>
              <CommandItem
                key="sex-deceased"
                value="sex-deceased"
                onSelect={() => setSelectedPersonType({ sex: 'unknown', deceased: true })}
                className="text-xs cursor-pointer"
              >
                {t('quickCreatePanel.deceased')}
              </CommandItem>
            </CommandGroup>
            <CommandGroup heading={t('quickCreatePanel.actionsHeading')}>
              <CommandItem
                key="link-existing-from-person"
                value="link-existing-from-person"
                onSelect={() => {
                  setIsLinkingMode(true);
                  setSelectedPersonType({ sex: 'unknown' });
                }}
                className="text-xs cursor-pointer"
                data-testid="tid.ui.quickCreate.linkExistingFromPerson"
              >
                {t('quickCreatePanel.linkToExistingEllipsis')}
              </CommandItem>
            </CommandGroup>
          </>
        )}

        {/* Search results (only when no subtype is locked and person type is selected for create variant, or in linking mode) */}
        {search && !selectedSubtypeId && (variant === 'edit-edge' || (variant === 'create' && (selectedPersonType || isLinkingMode))) && (
          <CommandGroup heading={t('quickCreatePanel.resultsHeading')}>
            {searchResults.map(r => (
              <CommandItem
                key={`sr-${r.subtype.id}`}
                value={r.subtype.id}
                onSelect={() => { 
                  setSelectedCategoryId(r.subtype.categoryId); 
                  handleSubtypeSelect(r.subtype.id);
                  setSearch('');
                }}
                className="text-xs cursor-pointer"
                data-testid={`tid.ui.quickCreate.subtype.${r.subtype.id}`}
              >
                {t(r.subtype.label as any, { defaultValue: r.subtype.label })}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Categories (only when person type selected for create, or always for edit-edge, and no active search) */}
        {!search && !selectedCategoryId && (variant === 'edit-edge' || (variant === 'create' && (selectedPersonType || isLinkingMode))) && (
          <CommandGroup heading={t('quickCreatePanel.categoriesHeading')}>
            {rankedCategories.map(c => (
              <CommandItem
                key={c.category.id}
                value={c.category.id}
                onSelect={() => { setSelectedCategoryId(c.category.id); setSelectedSubtypeId(null); setSearch(''); }}
                className="text-xs cursor-pointer"
                data-testid={`tid.ui.quickCreate.category.${c.category.id}`}
              >
                {t(c.category.label as any, { defaultValue: c.category.label })}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Subtypes (only when category selected, subtype not yet chosen, and not searching) */}
        {!search && selectedCategoryId && !selectedSubtypeId && (
          <CommandGroup heading={t('quickCreatePanel.subtypesHeading')}>
            {rankedSubtypes.map(s => (
              <CommandItem
                key={s.subtype.id}
                value={s.subtype.id}
                onSelect={() => handleSubtypeSelect(s.subtype.id)}
                className="text-xs cursor-pointer"
                data-testid={`tid.ui.quickCreate.subtype.${s.subtype.id}`}
              >
                {t(s.subtype.label as any, { defaultValue: s.subtype.label })}
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Link existing option (only when subtype selected and variant is create) */}
        {variant === 'create' && selectedSubtypeId && (
          <CommandGroup heading={t('quickCreatePanel.actionsHeading')}>
            <CommandItem
              key="link-existing"
              value="link-existing"
              onSelect={() => onLinkExisting({ subtypeId: selectedSubtypeId })}
              className="text-xs cursor-pointer"
              data-testid="tid.ui.quickCreate.linkExisting"
            >
              {t('quickCreatePanel.linkToExistingEllipsis')}
            </CommandItem>
          </CommandGroup>
        )}
      </Command>
    </div>
  );
};

